import { styled } from "@mui/material/styles";
import SaveCancelButtons from "designSystem/components/button/SaveCancelButtons";
import DataGridNewCard from "designSystem/components/dataGrid/DatGridNewCard";
import { CacheContext } from "designSystem/hooks";
import { ColorChip, CsiImage, DataGrid, DataGridCardProps, FlexColumn, GridColumn, GridRow, OverflowText, PaneFooter, PaneHeader, SearchTextField, Text } from "designSystem/index";
import { useLocale } from "designSystem/locale";
import { STANDARD_BORDER_STYLE } from "designSystem/styles/style-functions";
import html from "csi/html";
import locale from "csi/locale";
import { getImageSrc } from "csi/model/image";
import { confirmLeavePage } from "csi/router";
import { useStore } from "csi/state/hooks";
import { C8Node } from "csi/typings";
import _ from "lodash";
import { observer } from "mobx-react-lite";
import React, { ChangeEventHandler, Fragment, FunctionComponent, useState } from "react";
import AttributeCard, { AttributeCardSkeleton } from "./AttributeCard";
import { usePresentationPageStore } from "./main";
import { PRODUCT_PRESENTATION_SET } from "./PPService";
import SetEditErrorDialog from "./SetEditErrorDialog";
import SetEditForm from "./SetEditForm";
import PresentationSetsStore from "./store/PresentationSetsStore";
import { SetEditStoreContext } from "./store/SetEditStore";

const PREFIX = "PresentationSetsLayout";

const classes = {
	productInnerBox: `${PREFIX}productInnerBox`,
	productOuterBox: `${PREFIX}productOuterBox`,
};

const StyledFlexColumn = styled(FlexColumn)(({
	theme: { palette, spacing },
}) => ({
	[`& .${classes.productInnerBox}`]: {
		backgroundColor: palette.background.paper,
		border: STANDARD_BORDER_STYLE,
		borderRadius: 3,
		display: "flex",
		height: 64,
	},

	[`& .${classes.productOuterBox}`]: {
		backgroundColor: palette.grey[50],
		borderColor: palette.grey[200],
		borderRadius: 3,
		borderStyle: "solid",
		borderWidth: 1,
		padding: spacing(3),
	},
}));

const SelectedProduct = observer(() => {
	const { rightPaneStore: { selected } } = useStore();
	const { cache, color, colorSpec, isDefaultChip, product } = selected as PresentationSetsStore;
	const { productInnerBox, productOuterBox } = classes;

	return (
		<div className={productOuterBox}>
			<div className={productInnerBox}>
				<CsiImage
					borderRadius="3px 0 0 3px"
					flexShrink={0}
					height={64}
					node={color || product}
					width={64}
				/>
				<GridColumn
					grow
					spacing={2}
					spacingAround={3}
				>
					<OverflowText text={locale.getNodeConfiguredName(product, cache)} variant="h6" />
					{color && (
						<GridRow spacing={1}>
							<ColorChip isDefault={isDefaultChip} src={getImageSrc(colorSpec)} />
							<OverflowText text={locale.getNodeConfiguredName(color, cache)} />
						</GridRow>
					)}
				</GridColumn>
			</div>
		</div>
	);
});

const SetAttributeCard: FunctionComponent<DataGridCardProps<C8Node>> = (props) => {
	const { item } = props;
	const { rightPaneStore: { selected } } = useStore();
	const { selectSet, setSpecs } = selected as PresentationSetsStore;

	return (
		<AttributeCard
			cardSpecs={setSpecs}
			onSelect={() => selectSet(item.$URL)}
			{...props}
		/>
	);
};

const SetAttributeCardSkeleton = () => {
	const { rightPaneStore: { selected } } = useStore();
	const { setSpecs } = selected as PresentationSetsStore;

	return (
		<AttributeCardSkeleton cardSpecs={setSpecs} />
	);
};

const SetAttributeNewCard = () => {
	const { rightPaneStore: { selected } } = useStore();
	const { selectSet } = selected as PresentationSetsStore;

	return (
		<DataGridNewCard
			minHeight={150}
			nodeType={PRODUCT_PRESENTATION_SET}
			onClick={() => selectSet()}
		/>
	);
};

const PresentationSetsContent = observer(() => {
	const { rightPaneStore: { selected } } = useStore();
	const {
		productType,
		setsStore,
		setSearchText,
	} = selected as PresentationSetsStore;

	const typeName = useLocale().type(productType);

	const onChangeText: ChangeEventHandler = _.debounce((event) => {
		setSearchText(event.target.value);
	}, 1000);

	return (
		<GridColumn
			grow
			scroll
			spacing={3}
			spacingAround={3}
		>
			<GridColumn spacing={1}>
				<Text
					color="textSecondary"
					res="PPSelectedProduct"
					resParams={[typeName]}
					variant="subtitle2"
				/>
				<SelectedProduct />
			</GridColumn>
			<GridColumn spacing={2}>
				<Text res="PPCreateSetForProduct" resParams={[typeName]} />
				<SearchTextField
					autoFocus
					fullWidth
					onChange={onChangeText}
					placeholderRes="Search"
				/>
			</GridColumn>
			<CacheContext.Provider value={setsStore.cache}>
				<DataGrid
					CardComponent={SetAttributeCard}
					NewCardComponent={SetAttributeNewCard}
					SkeletonComponent={SetAttributeCardSkeleton}
					columns={2}
					spacing={3}
					store={setsStore}
				/>
			</CacheContext.Provider>
		</GridColumn>
	);
});

const PresentationSetsLayout = observer(() => {
	const { rightPaneStore: { selected, closeSelected } } = useStore();
	const { config: { confirmLeaving }, editSetStore, onBack, onSaveSet, showBackButton } = selected as PresentationSetsStore;
	const { recalculateViolations } = usePresentationPageStore() || {};
	const [isFailing, setFailing] = useState<boolean>(false);

	const checkState = async (callback: () => void) => {
		if (confirmLeaving()) {
			try {
				await confirmLeavePage();
			} catch (ex) {
				return;
			}
		}
		callback();
	};

	const onSave = async () => {
		try {
			html.showWait(true);

			const status = await editSetStore.saveChanges();
			if (status) {
				// close the pane
				closeSelected();
			} else {
				setFailing(true);
			}
		} catch (err) {
			throw err;
		} finally {
			html.showWait(false);

			// callback
			onSaveSet();

			if (editSetStore.hasChanged) {
				recalculateViolations();
			}
		}

		// close right panel when saved rather than go back, so return false when done
		return false;
	};

	const onValidate = async () => {
		return editSetStore.validateChanges();
	};

	const onClose = () => closeSelected();

	return (
		<StyledFlexColumn>
			<PaneHeader
				back={showBackButton}
				onBack={() => checkState(onBack)}
				onClose={() => checkState(closeSelected)}
				titleRes={editSetStore ? "PPEditSet" : "PPSelectSet"}
			/>
			{isFailing && (
				<SetEditErrorDialog
					editSetStore={editSetStore}
					onClose={onClose}
				/>
			)}
			{editSetStore ? (
				<Fragment>
					<SetEditStoreContext.Provider value={editSetStore}>
						<SetEditForm />
					</SetEditStoreContext.Provider>
					<PaneFooter>
						<SaveCancelButtons
							onCancel={onClose}
							onSave={onSave}
							onValidate={onValidate}
						/>
					</PaneFooter>
				</Fragment>
			)
				:
				<PresentationSetsContent />
			}
		</StyledFlexColumn>
	);
});

export default PresentationSetsLayout;